import { ReactNode } from "react";
import { Box, BoxProps, Typography } from "@mui/material";
import { PAGE_GUTTER_X, PAGE_MAX_WIDTH, PageWidth } from "./pageLayout";

interface PageProps extends Omit<BoxProps, "maxWidth"> {
  width?: PageWidth;
  children: ReactNode;
}

/** Root wrapper for every routed page: consistent gutters, vertical rhythm and max width. */
export function Page({ width = "full", children, sx, ...rest }: PageProps) {
  return (
    <Box
      {...rest}
      sx={[
        {
          width: "100%",
          maxWidth: PAGE_MAX_WIDTH[width],
          mx: "auto",
          px: PAGE_GUTTER_X,
          pt: { xs: 2, md: 3 },
          // Clears the fixed floating buttons (Quote History bottom-left, AI Analyst bottom-right) so the last table row can scroll out from under them.
          pb: 12,
          boxSizing: "border-box",
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {children}
    </Box>
  );
}

interface PageHeaderProps {
  title: ReactNode;
  /** One or two lines explaining what the page is for. */
  subtitle?: ReactNode;
  /** Right-aligned controls (buttons, selects, status indicators). Wraps below the title on narrow screens. */
  actions?: ReactNode;
}

/** Page title row: h1 (theme h4), optional subtitle, optional right-aligned actions. */
export function PageHeader({ title, subtitle, actions }: PageHeaderProps) {
  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        justifyContent: "space-between",
        columnGap: 3,
        rowGap: 1.5,
        mb: 3,
      }}
    >
      <Box sx={{ minWidth: 0, flex: "1 1 320px" }}>
        <Typography variant="h4" component="h1">
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: "100ch" }}>
            {subtitle}
          </Typography>
        )}
      </Box>
      {actions && (
        <Box
          sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1, flex: "0 0 auto" }}
        >
          {actions}
        </Box>
      )}
    </Box>
  );
}
